import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Clipboard } from '@angular/cdk/clipboard';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { downloadBlobUrlFileToBrowser } from '@highlander/common/helpers/file.helpers';
import { CommonDataType } from '@models/common';
import { CreateDocumentRequest, Document, DocumentCommonDataObject, DocumentListItem, UpdateDocumentRequest } from '@models/documents';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { BlobStorageService, NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { AppConfigService } from 'src/app/infrastructure/app-config.service';
import { createCustomStateSetOnUrlHandler, createStateResetHandler, deleteEntity, getAll, getOne, StateDictionary } from '../helpers';
import { DocumentsService } from './documents.service';

type DocumentsState = {
  documents: DocumentListItem[];
  selectedDocument: Document | null;
  isLoading: boolean;
  isDocumentCreateLoading: boolean;
  isSelectedDocumentLoading: boolean;
  isFileUploadLoading: boolean;
};

const initialState: DocumentsState = {
  documents: [],
  selectedDocument: null,
  isLoading: false,
  isDocumentCreateLoading: false,
  isSelectedDocumentLoading: false,
  isFileUploadLoading: false,
};

const documentsDictionary: StateDictionary<Document> = {
  entityList: 'documents',
  listLoading: 'isLoading',
  createLoading: 'isDocumentCreateLoading',
  selectedEntity: 'selectedDocument',
  selectedEntityLoading: 'isSelectedDocumentLoading',
  entityId: 'id',
};

export const DocumentsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('documents-store'),
  withComputed(store => ({
    selectedDocumentId: computed(() => store.selectedDocument()?.id || null),
  })),
  withMethods(
    (
      store,
      documentsService = inject(DocumentsService),
      router = inject(Router),
      blobStorageService = inject(BlobStorageService),
      clipboard = inject(Clipboard),
      appConfig = inject(AppConfigService),
      notifications = inject(NotificationsService),
    ) => {
      createStateResetHandler<DocumentsState>('/marketing/documents')(store, initialState);
      createCustomStateSetOnUrlHandler<DocumentsState>('/marketing/documents', { selectedDocument: null })(store);

      const repositoryMethods = {
        getAll: getAll<DocumentListItem>({
          store,
          service: documentsService,
          dictionary: documentsDictionary as unknown as StateDictionary<DocumentListItem>,
        }),

        getOne: getOne<Document>({
          store,
          service: documentsService,
          dictionary: documentsDictionary,
        }),

        createDocument: rxMethod<{ docDto: CreateDocumentRequest; file: File }>(
          pipe(
            tap(() => patchState(store, { isDocumentCreateLoading: true })),
            switchMap(({ docDto, file }) =>
              documentsService.add(docDto).pipe(
                switchMap(response =>
                  blobStorageService.uploadFile(response.URL, file).pipe(
                    switchMap(() => documentsService.getOne(response.id)),
                    tap(fullDocument => {
                      patchState(store, {
                        selectedDocument: fullDocument,
                        isFileUploadLoading: false,
                        isDocumentCreateLoading: false,
                      });
                      repositoryMethods.getAll();
                      router.navigate(['marketing', 'documents', fullDocument.id, 'document-info']);
                    }),
                    catchError(() => {
                      return documentsService.delete(response.id).pipe(
                        tap(() => {
                          patchState(store, {
                            isFileUploadLoading: false,
                            isDocumentCreateLoading: false,
                          });
                        }),
                        catchError(() => {
                          patchState(store, {
                            isFileUploadLoading: false,
                            isDocumentCreateLoading: false,
                          });
                          return EMPTY;
                        }),
                      );
                    }),
                  ),
                ),
                catchError(() => {
                  patchState(store, { isDocumentCreateLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateDocument: rxMethod<{ id: string; dto: UpdateDocumentRequest }>(
          pipe(
            tap(() => patchState(store, { isSelectedDocumentLoading: true })),
            switchMap(({ id, dto }) =>
              documentsService.update(id, dto).pipe(
                tap(updatedDoc => {
                  patchState(store, {
                    selectedDocument: updatedDoc,
                    isSelectedDocumentLoading: false,
                  });
                  repositoryMethods.getAll();
                }),
                catchError(() => {
                  patchState(store, { isSelectedDocumentLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteDocument: deleteEntity<Document>({
          store,
          service: documentsService,
          dictionary: documentsDictionary,
          onSuccessCallback: () => {
            router.navigate(['marketing', 'documents']);
            patchState(store, { selectedDocument: null });
          },
        }),

        publishDocument: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isSelectedDocumentLoading: true })),
            switchMap(id =>
              documentsService.publish(id).pipe(
                tap(updatedDoc => {
                  patchState(store, {
                    selectedDocument: updatedDoc,
                    isSelectedDocumentLoading: false,
                  });
                  repositoryMethods.getAll();
                }),
                catchError(() => {
                  patchState(store, { isSelectedDocumentLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        unpublishDocument: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isSelectedDocumentLoading: true })),
            switchMap(id =>
              documentsService.unpublish(id).pipe(
                tap(updatedDoc => {
                  patchState(store, {
                    selectedDocument: updatedDoc,
                    isSelectedDocumentLoading: false,
                  });
                  repositoryMethods.getAll();
                }),
                catchError(() => {
                  patchState(store, { isSelectedDocumentLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        uploadFile: rxMethod<{ id: string; file: File; fileName: string }>(
          pipe(
            tap(() => patchState(store, { isFileUploadLoading: true })),
            switchMap(({ id, file, fileName }) =>
              documentsService.uploadNewFileToExistingDoc(id, fileName).pipe(
                switchMap(response =>
                  blobStorageService.uploadFile(response.URL, file).pipe(
                    tap(() => {
                      patchState(store, {
                        selectedDocument: response.document,
                        isFileUploadLoading: false,
                      });
                      router.navigate(['marketing', 'documents', id, 'document-info']);
                    }),
                  ),
                ),
                catchError(() => {
                  patchState(store, { isFileUploadLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        downloadDocument: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isLoading: true, isSelectedDocumentLoading: true })),
            switchMap(id =>
              documentsService.download(id).pipe(
                tap(response => {
                  downloadBlobUrlFileToBrowser(response.URL);
                  patchState(store, { isLoading: false, isSelectedDocumentLoading: false });
                }),
                catchError(() => {
                  patchState(store, { isLoading: false, isSelectedDocumentLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        copyExternalLink: () => {
          const doc = store.selectedDocument();
          if (doc) {
            clipboard.copy(`${appConfig.config['FRONTEND_PORTAL_URL']}/documents/${doc.userFriendlyName}/download`);
            notifications.showNotification('Copied successfully.');
          }
        },

        updateCommonData: rxMethod<{ docId: string; type: CommonDataType; data: DocumentCommonDataObject[] }>(
          pipe(
            tap(() => patchState(store, { isSelectedDocumentLoading: true })),
            switchMap(({ docId, type, data }) =>
              documentsService.updateCommonData(docId, type, data).pipe(
                tap(updatedDoc => {
                  patchState(store, {
                    selectedDocument: updatedDoc,
                    isSelectedDocumentLoading: false,
                  });
                  repositoryMethods.getAll();
                }),
                catchError(() => {
                  patchState(store, { isSelectedDocumentLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationMethods = {
        goToListPage: () => {
          router.navigate(['marketing', 'documents']);
          patchState(store, { selectedDocument: null });
        },

        goToDocumentActions: async (docId: string): Promise<boolean> => {
          const result = await router.navigate(['marketing', 'documents', docId]);
          if (!result) return false;

          if (!store.isSelectedDocumentLoading()) repositoryMethods.getOne(docId);
          return true;
        },

        goToDocumentInformation: async (docId: string): Promise<boolean> => {
          return await router.navigate(['marketing', 'documents', docId, 'document-info']);
        },
      };

      return {
        ...repositoryMethods,
        ...navigationMethods,
      };
    },
  ),
);

export interface DocumentsStoreType {
  readonly documents: Signal<DocumentListItem[]>;
  readonly selectedDocument: Signal<Document | null>;
  readonly isLoading: Signal<boolean>;
  readonly isDocumentCreateLoading: Signal<boolean>;
  readonly isSelectedDocumentLoading: Signal<boolean>;
  readonly isFileUploadLoading: Signal<boolean>;

  readonly selectedDocumentId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly createDocument: (params: { docDto: CreateDocumentRequest; file: File }) => void;
  readonly updateDocument: (params: { id: string; dto: UpdateDocumentRequest }) => void;
  readonly deleteDocument: (id: string) => void;
  readonly downloadDocument: (id: string) => void;
  readonly copyExternalLink: () => void;
  readonly updateCommonData: (params: { docId: string; type: CommonDataType; data: DocumentCommonDataObject[] }) => void;

  readonly publishDocument: (id: string) => void;
  readonly unpublishDocument: (id: string) => void;
  readonly uploadFile: (params: { id: string; file: File; fileName: string }) => void;

  readonly goToListPage: () => void;
  readonly goToDocumentActions: (id: string) => Promise<boolean>;
  readonly goToDocumentInformation: (id: string) => Promise<boolean>;
}
